/**
 * /gatherings/:gatheringId/manage (+ /clubs/:id/gatherings/:gatheringId/manage) — the gathering's
 * OWN manager (Oct 2026, native admissions). OVERVIEW · ATTENDEES · APPLICATIONS · DOOR · MONEY
 * · EDIT on the Portal vocabulary (clubUi panels, RingTab tabs, two-step confirms, a toast).
 *
 * The parent is never an event here: entry (gathering admissions, seats, the gathering door,
 * entry money) and each child event's own attendance / scanner / money are shown side by side
 * and reached separately — a child's tools open ITS EventOperationPage, the parent's never do.
 * EDIT is the gathering wizard. Legacy (pass-event) gatherings get the same surfaces fed by the
 * compatibility endpoints; what only native rows can answer (seats, held money) says so.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useDialog } from '../context/DialogContext';
import LoadingSpinner from '../components/LoadingSpinner';
import AddPeopleModal from '../components/AddPeopleModal';
import gatheringService from '../services/gatheringService';
import useIsMobile from '../hooks/useIsMobile';
import useDoorScanner from '../hooks/useDoorScanner';
import DoorScannerCard, { btnDark } from '../components/door/DoorScannerCard';
import { SurfacePage, SoftSurface, EmptyBox } from '../components/surface';
import { ClubPanel, PanelHead, PillButton, RingTab, TagPill, Toast, Face, EmptyBlock, useArmed, useToast, INK, MUTED, SUBTLE, BODY, CHIP, INSET, CYAN, GREEN, RED, RULE, label as lbl } from '../components/club/clubUi';
import { Bar, KVRows, kicker, STARTER, STARTER_TRACK } from '../components/gathering/gatheringUi';
import { gatheringStatus, STATUS_PILL } from '../components/gathering/gatheringModel';
import { money, dayRangeLabel, whenLabel, plural, ago } from '../utils/surfaceFormat';

const err = (r) => r?.error?.message || r?.error || 'Something went wrong.';

export const TABS = [['overview', 'OVERVIEW'], ['attendees', 'ATTENDEES'], ['applications', 'APPLICATIONS'], ['door', 'DOOR'], ['money', 'MONEY'], ['edit', 'EDIT']];

/** Entry status → pill. */
export const ENTRY_PILL = {
  paid: ['IN', '#22C55E', '#fff'], accepted: ['ACCEPTED · UNPAID', '#FACC15', INK], pending_payment: ['AWAITING PAYMENT', '#FACC15', INK], pending: ['WAITING', CHIP, INK],
  payment_failed: ['PAYMENT FAILED', '#FEE2E2', RED], declined: ['DECLINED', CHIP, MUTED], withdrawn: ['WITHDREW', CHIP, MUTED],
  refund_pending: ['REFUNDING', '#FEE2E2', RED], refund_failed: ['REFUND FAILED', RED, '#fff'], refunded: ['REFUNDED', CHIP, MUTED],
  going: ['GOING', '#22C55E', '#fff'],
};
export const entryPill = (status) => ENTRY_PILL[status] || [String(status || '').toUpperCase().replace(/_/g, ' '), CHIP, INK];

const LIFECYCLE_PILL = {
  selling: ['SELLING', '#22C55E', '#fff'], funding: ['FUNDING', '#FACC15', INK], greenlit: ['GREENLIT', '#A3E635', INK], draft: ['DRAFT', '#fff', INK],
  ended: ['ENDED', '#E5E7EB', MUTED], canceled: ['CANCELED', RED, '#fff'], dissolved: ['DISSOLVED', '#E5E7EB', MUTED], legacy: ['ENTRY VIA PASS EVENT', CHIP, MUTED],
};

export default function GatheringManagePage() {
  const navigate = useNavigate();
  const { id: routeClubId, gatheringId } = useParams();
  const [params, setParams] = useSearchParams();
  const { profile } = useUser();
  const { showAlert } = useDialog();
  const mobile = useIsMobile();
  const { toast, say } = useToast();
  const { confirm, isArmed } = useArmed(6000);

  const [g, setG] = useState(null);
  const [roster, setRoster] = useState(null);
  const [moneyDto, setMoneyDto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(null);
  const [compOpen, setCompOpen] = useState(false);
  const [attendeeView, setAttendeeView] = useState('person'); // person | event
  const [q, setQ] = useState('');
  const [doorVerdicts, setDoorVerdicts] = useState({});

  const tab = TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'overview';
  const setTab = (k) => {
    if (k === 'edit') { navigate(editPath); return; }
    const next = new URLSearchParams(params);
    next.set('tab', k);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    const r = await gatheringService.get(gatheringId);
    if (!r.success || !r.data) { setG(null); setLoading(false); return; }
    if (!r.data.isHost) { setForbidden(true); setLoading(false); return; }
    setG(r.data);
    setLoading(false);
  }, [gatheringId]);
  const loadRoster = useCallback(async () => {
    const r = await gatheringService.getRoster(gatheringId);
    if (r.success) setRoster(r.data);
  }, [gatheringId]);
  const loadMoney = useCallback(async () => {
    const r = await gatheringService.getMoney(gatheringId);
    if (r.success) setMoneyDto(r.data);
  }, [gatheringId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (g) { loadRoster(); loadMoney(); } }, [g, loadRoster, loadMoney]);
  const refresh = () => { load(); };

  const clubId = routeClubId || g?.clubId;
  const isClub = !!g?.clubId;
  const pagePath = isClub && clubId ? `/clubs/${clubId}/gatherings/${gatheringId}` : `/gatherings/${gatheringId}`;
  const editPath = isClub && clubId ? `/clubs/${clubId}/gatherings/${gatheringId}/edit` : `/gatherings/${gatheringId}/edit`;

  // ── Door (native gathering entry only) ──
  const doorApi = useMemo(() => ({
    scan: async (code) => {
      const r = await gatheringService.doorScan(gatheringId, code);
      return r.success ? { success: true, data: { ...r.data, guestName: r.data?.displayName } } : r;
    },
    checkIn: async (admissionId) => {
      const r = await gatheringService.doorCheckIn(gatheringId, admissionId);
      return r.success ? { success: true, data: { ...r.data, guestName: r.data?.displayName } } : r;
    },
  }), [gatheringId]);
  const onDoorResult = useCallback((r) => { if (r.result === 'ok') loadRoster(); }, [loadRoster]);
  const scanner = useDoorScanner({ eventId: gatheringId, kind: 'gathering', regionId: 'gathering-door-region', onResult: onDoorResult, api: doorApi });

  const frame = (children) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1180, padding: mobile ? '12px 12px 24px' : '24px 32px 72px', gap: mobile ? 10 : 16, boxSizing: 'border-box', color: INK }} data-testid="gathering-manager">
          {children}
        </main>
        <Toast text={toast} phone={mobile} bottom={mobile ? 150 : 32} />
      </SurfacePage>
    </SoftSurface>
  );
  if (loading) return frame(<div className="flex justify-center py-20"><LoadingSpinner /></div>);
  if (forbidden) return frame(<EmptyBox title="THIS ISN'T YOURS TO RUN." mobile={mobile} action={<PillButton size={40} onClick={() => navigate(pagePath)}>← THE GATHERING</PillButton>}>Only the gathering's host sees the manager.</EmptyBox>);
  if (!g) return frame(<EmptyBox title="THIS GATHERING ISN'T AVAILABLE." mobile={mobile} action={<PillButton size={40} onClick={() => navigate('/host')}>← HOST DASHBOARD</PillButton>}>It may have been removed.</EmptyBox>);

  // ── Derived ──
  const native = !!g.nativeAdmissions;
  const hasEntry = !!g.requiresApplication;
  const status = gatheringStatus(g);
  const statusPill = STATUS_PILL[status] || STATUS_PILL.draft;
  const lifecycle = moneyDto?.lifecycle || (native ? 'draft' : 'legacy');
  const [lcLabel, lcBg, lcFg] = LIFECYCLE_PILL[lifecycle] || LIFECYCLE_PILL.legacy;
  const agenda = g.agenda || [];
  const applicants = g.applicants || [];
  const admissions = roster?.admissions || [];
  const entryIn = (native ? admissions : applicants).filter((a) => a.status === 'paid').length;
  const pending = applicants.filter((a) => a.status === 'pending').length;
  const starter = g.fundingType === 'party_starter';
  const canGreenlight = hasEntry && starter && (lifecycle === 'funding' || !native);
  const goalPct = g.fundingGoalCents ? Math.min(100, Math.round(((g.pledgedCents || 0) / g.fundingGoalCents) * 100)) : 0;
  const open = !['ended', 'canceled', 'dissolved'].includes(lifecycle) && g.status !== 'ended';

  // ── Actions ──
  const review = async (userId, action) => {
    setBusy(`${userId}:${action}`);
    const r = await gatheringService.reviewApplicant(gatheringId, userId, action);
    setBusy(null);
    if (!r.success) { await showAlert({ title: 'COULD NOT UPDATE', message: err(r), type: 'error' }); return; }
    say(action === 'accept' ? 'ACCEPTED' : 'DECLINED');
    refresh();
  };
  const refund = async (a) => {
    if (!confirm(`refund-${a.admissionId}`)) return;
    setBusy(`refund:${a.admissionId}`);
    const r = await gatheringService.refundAdmission(gatheringId, a.admissionId, 'Refunded by the host');
    setBusy(null);
    if (!r.success) { await showAlert({ title: 'COULD NOT REFUND', message: err(r), type: 'error' }); return; }
    say(r.data?.status === 'settled' ? 'REFUNDED' : `REFUND ${String(r.data?.status || '').toUpperCase()}`);
    refresh();
  };
  const comp = async (userIds) => {
    setCompOpen(false);
    const results = await Promise.all((userIds || []).map((id) => gatheringService.comp(gatheringId, id)));
    const failed = results.find((x) => !x.success);
    if (failed) await showAlert({ title: 'COULD NOT COMP', message: err(failed), type: 'error' });
    else say('COMPED');
    refresh();
  };
  const greenlight = async () => {
    if (!confirm('greenlight')) return;
    setBusy('greenlight');
    const r = await gatheringService.greenlight(gatheringId);
    setBusy(null);
    if (!r.success) { await showAlert({ title: 'COULD NOT GREENLIGHT', message: err(r), type: 'error' }); return; }
    say('GREENLIT');
    refresh();
  };
  const cancel = async () => {
    if (!confirm('cancel')) return;
    setBusy('cancel');
    const r = await gatheringService.cancel(gatheringId);
    setBusy(null);
    if (!r.success) { await showAlert({ title: 'COULD NOT CANCEL', message: err(r), type: 'error' }); refresh(); return; }
    say('CANCELED · ENTRIES REFUNDING');
    refresh();
  };
  const doorCheckIn = async (a) => {
    setBusy(`door:${a.id}`);
    const data = await scanner.checkIn(a.id);
    setBusy(null);
    setDoorVerdicts((v) => ({ ...v, [a.id]: data }));
  };

  // ── Header ──
  const header = (
    <ClubPanel phone={mobile} data-testid="manager-header">
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}>
        <button type="button" onClick={() => navigate(pagePath)} className="text-left" style={{ ...kicker(10), border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' }} data-testid="back-to-gathering">← THE GATHERING</button>
        <span className="flex flex-wrap" style={{ gap: 6 }}>
          <PillButton variant="tertiary" size={36} onClick={() => navigate(pagePath)} data-testid="view-page">VIEW PAGE</PillButton>
          <PillButton size={36} onClick={() => navigate(editPath)} data-testid="edit-gathering">EDIT</PillButton>
        </span>
      </span>
      <span className="flex flex-col" style={{ gap: 8 }}>
        <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <TagPill bg={CYAN} size={9}>GATHERING MANAGER</TagPill>
          <TagPill bg={statusPill.bg} fg={statusPill.fg} ring={statusPill.ring} size={9}>{statusPill.label}</TagPill>
          <TagPill bg={lcBg} fg={lcFg} size={9} data-testid="lifecycle-pill">{lcLabel}</TagPill>
        </span>
        <h1 style={{ margin: 0, textTransform: 'none', fontSize: mobile ? 24 : 34, fontWeight: 700, lineHeight: 1.1, overflowWrap: 'anywhere' }}>{g.name}</h1>
        <span style={{ fontSize: 12, color: MUTED }}>{dayRangeLabel(g.startsAt, g.endsAt)} · {plural(agenda.length, 'EVENT')}{isClub && g.clubName ? ` · ${g.clubName}` : ''}</span>
      </span>
    </ClubPanel>
  );

  const tabs = TABS.filter(([k]) => (k !== 'applications' && k !== 'door') || hasEntry);
  const tabRow = (
    <div role="tablist" aria-label="Gathering manager" className="flex" style={{ flexWrap: mobile ? 'nowrap' : 'wrap', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} data-testid="manager-tabs">
      {tabs.map(([k, l]) => <RingTab key={k} role="tab" on={tab === k} onClick={() => setTab(k)} badge={k === 'applications' && pending ? pending : undefined} badgeBg={k === 'applications' && pending ? '#FACC15' : undefined} data-testid={`tab-${k}`}>{l}</RingTab>)}
    </div>
  );

  const stat = (n, k, color = INK, testId) => (
    <span className="flex flex-col" style={{ gap: 2, minWidth: 90 }} data-testid={testId}>
      <span style={{ fontSize: mobile ? 22 : 28, fontWeight: 800, lineHeight: 1, color }}>{n}</span>
      <span style={{ ...kicker(9), color: MUTED }}>{k}</span>
    </span>
  );

  // ── OVERVIEW ──
  const eventRow = (e) => {
    const ev = roster?.events?.find((x) => x.eventId === e.id);
    return (
      <div key={e.id} className="flex flex-wrap items-center" style={{ gap: '8px 14px', padding: '12px 0', borderTop: RULE }} data-testid={`agenda-${e.id}`}>
        <span className="flex min-w-0 flex-col" style={{ flex: '1 1 240px', gap: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>{e.title}</span>
          <span style={{ fontSize: 11, color: MUTED }}>{whenLabel(e.dateTime)}{e.venueName ? ` · ${e.venueName}` : ''} · {e.ticketPriceCents > 0 ? money(e.ticketPriceCents) : e.isExternalTicketing ? 'EXTERNAL' : 'FREE · RSVP'}</span>
        </span>
        <span style={{ ...kicker(9), color: MUTED }}>{ev ? plural(ev.guestCount, e.ticketPriceCents > 0 ? 'TICKET HOLDER' : 'GOING') : `${e.ticketPriceCents > 0 ? e.ticketCount : e.goingCount} ${e.ticketPriceCents > 0 ? 'HELD' : 'GOING'}`}</span>
        <span className="flex" style={{ gap: 6 }}>
          <PillButton variant="tertiary" size={32} onClick={() => navigate(`/host/events/${e.id}`)} data-testid={`manage-event-${e.id}`}>MANAGE EVENT</PillButton>
          {e.ticketPriceCents > 0 && <PillButton variant="ghost" size={32} onClick={() => navigate(`/host/events/${e.id}/door`)} data-testid={`scan-event-${e.id}`}>ITS SCANNER</PillButton>}
        </span>
      </div>
    );
  };
  const overview = (
    <>
      <ClubPanel phone={mobile} data-testid="overview-stats">
        <PanelHead title="AT A GLANCE" right={native ? 'NATIVE ENTRY' : hasEntry ? 'ENTRY VIA PASS EVENT' : 'NO ENTRY · RSVP ONLY'} />
        <div className="flex flex-wrap" style={{ gap: mobile ? 16 : 28 }}>
          {hasEntry && stat(entryIn, 'IN · ENTRY', GREEN, 'stat-entry')}
          {hasEntry && native && stat(`${roster?.seatsCheckedIn ?? 0}/${roster?.seatsTotal ?? 0}`, 'SEATS SCANNED', INK, 'stat-seats')}
          {hasEntry && stat(pending, 'WAITING', pending ? '#B45309' : INK, 'stat-pending')}
          {stat(agenda.length, 'EVENTS', INK, 'stat-events')}
          {hasEntry && stat(moneyDto ? money(moneyDto.heldCents || 0) : '—', native ? 'HELD IN ESCROW' : 'HELD', INK, 'stat-held')}
        </div>
        <span style={{ fontSize: 11, lineHeight: 1.6, color: BODY }}>Entry to the gathering and attendance at each event are separate. A gathering seat never admits anyone to a ticketed event, and an event ticket never opens the gathering door.</span>
      </ClubPanel>
      {hasEntry && starter && (
        <ClubPanel phone={mobile} data-testid="funding-panel">
          <PanelHead title="PARTY STARTER" right={g.fundingDeadline ? `DEADLINE ${whenLabel(g.fundingDeadline)}` : null} />
          <span className="flex items-baseline justify-between" style={{ gap: 8 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>{money(g.pledgedCents || 0)} <span style={{ fontSize: 11, color: MUTED }}>PLEDGED</span></span>
            <span style={{ fontSize: 11, color: MUTED }}>GOAL {money(g.fundingGoalCents || 0)} · {goalPct}%</span>
          </span>
          <Bar pct={goalPct} track={STARTER_TRACK} fill={STARTER} />
          {canGreenlight && (
            <PillButton variant={isArmed('greenlight') ? 'armed' : 'ring'} size={44} disabled={busy === 'greenlight'} onClick={greenlight} data-testid="greenlight">
              {isArmed('greenlight') ? 'TAP AGAIN TO GREENLIGHT' : busy === 'greenlight' ? 'GREENLIGHTING…' : 'GREENLIGHT'}
            </PillButton>
          )}
        </ClubPanel>
      )}
      <ClubPanel phone={mobile} data-testid="agenda-panel">
        <PanelHead title="THE AGENDA" right={plural(agenda.length, 'EVENT')} />
        {agenda.length === 0 && <EmptyBlock title="NOTHING ON THE AGENDA YET." body="Add events in EDIT. Each one gets its own manager and scanner." action="EDIT" onAction={() => navigate(editPath)} />}
        <div className="flex flex-col">{agenda.map(eventRow)}</div>
      </ClubPanel>
      {native && open && hasEntry && (
        <ClubPanel phone={mobile} data-testid="danger-panel">
          <PanelHead title="CALL IT OFF" />
          <span style={{ fontSize: 11, lineHeight: 1.6, color: BODY }}>Cancelling closes entry and refunds every paid entry to where it came from. The events on the agenda are untouched — cancel those from their own managers.</span>
          <PillButton variant={isArmed('cancel') ? 'armed' : 'danger'} size={40} disabled={busy === 'cancel'} onClick={cancel} style={{ alignSelf: 'flex-start' }} data-testid="cancel-gathering">
            {isArmed('cancel') ? 'TAP AGAIN TO CANCEL THE GATHERING' : busy === 'cancel' ? 'CANCELLING…' : 'CANCEL THE GATHERING'}
          </PillButton>
        </ClubPanel>
      )}
    </>
  );

  // ── ATTENDEES ──
  const people = roster?.people || [];
  const filteredPeople = people.filter((p) => !q.trim() || p.displayName.toLowerCase().includes(q.trim().toLowerCase()) || (p.handle || '').toLowerCase().includes(q.trim().toLowerCase()));
  const titleOf = (eventId) => agenda.find((e) => e.id === eventId)?.title || 'Event';
  const attendees = (
    <ClubPanel phone={mobile} data-testid="attendees-panel">
      <PanelHead title="WHO'S IN" right={`${people.length} PEOPLE`}>
        <span role="radiogroup" aria-label="Attendee view" className="flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: INSET }}>
          {[['person', 'BY PERSON'], ['event', 'BY EVENT']].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={attendeeView === k} onClick={() => setAttendeeView(k)} data-testid={`attendees-${k}`}
              style={{ height: 30, padding: '0 12px', border: '2px solid transparent', borderRadius: 999, background: attendeeView === k ? INK : 'transparent', color: attendeeView === k ? '#fff' : INK, fontFamily: 'inherit', fontSize: 9, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer' }}>{l}</button>
          ))}
        </span>
      </PanelHead>
      {!roster && <span style={{ fontSize: 12, color: MUTED }}>Loading…</span>}
      {roster && attendeeView === 'person' && (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person…" aria-label="Find a person" style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 999, background: INSET, fontFamily: 'inherit', fontSize: 13, outline: 'none', maxWidth: 360 }} />
          {filteredPeople.length === 0 && <EmptyBlock title="NO ONE YET." body={hasEntry ? 'People show up here once they get in or RSVP to an event.' : 'People show up here once they RSVP or buy into an event.'} />}
          <div className="flex flex-col">
            {filteredPeople.map((p) => {
              const [pl, pbg, pfg] = p.entryStatus ? entryPill(p.entryStatus) : ['NO ENTRY', CHIP, MUTED];
              return (
                <div key={p.userId} className="flex flex-wrap items-center" style={{ gap: '6px 12px', padding: '10px 0', borderTop: RULE }} data-testid={`person-${p.userId}`}>
                  <Face src={p.avatarUrl} name={p.displayName} size={36} />
                  <span className="flex min-w-0 flex-col" style={{ flex: '1 1 160px', gap: 2 }}>
                    <button type="button" onClick={() => p.handle && navigate(`/${p.handle}`)} className="text-left" style={{ fontSize: 13, fontWeight: 700, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: p.handle ? 'pointer' : 'default' }}>{p.displayName}</button>
                    {p.handle && <span style={{ fontSize: 10, color: MUTED }}>@{p.handle}</span>}
                  </span>
                  {hasEntry && <TagPill bg={pbg} fg={pfg} size={8} data-testid={`person-entry-${p.userId}`}>{pl}</TagPill>}
                  <span className="flex flex-wrap" style={{ gap: 4, flex: '1 1 200px' }}>
                    {p.eventIds.length === 0 && <span style={{ fontSize: 10, color: SUBTLE }}>NO EVENTS YET</span>}
                    {p.eventIds.map((id) => <TagPill key={id} size={8}>{titleOf(id)}</TagPill>)}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
      {roster && attendeeView === 'event' && (
        <div className="flex flex-col" style={{ gap: 14 }}>
          {hasEntry && (
            <div className="flex flex-col" style={{ gap: 6 }} data-testid="by-event-entry">
              <span className="flex items-center justify-between"><span style={lbl(10)}>GATHERING ENTRY</span><span style={{ ...kicker(9), color: MUTED }}>{plural(admissions.filter((a) => a.status === 'paid').length, 'IN')}</span></span>
              {admissions.length === 0 && <span style={{ fontSize: 11, color: SUBTLE }}>{native ? 'No entries yet.' : 'Entry rows live on the pass event until the gathering is migrated.'}</span>}
              <span className="flex flex-wrap" style={{ gap: 6 }}>{admissions.map((a) => { const [pl, pbg, pfg] = entryPill(a.status); return <span key={a.id} className="flex items-center" style={{ gap: 6, padding: '4px 10px 4px 4px', borderRadius: 999, background: INSET, fontSize: 11, fontWeight: 700 }}><Face src={a.avatarUrl} name={a.displayName} size={24} />{a.displayName}<TagPill bg={pbg} fg={pfg} size={7}>{pl}</TagPill></span>; })}</span>
            </div>
          )}
          {(roster.events || []).map((ev) => (
            <div key={ev.eventId} className="flex flex-col" style={{ gap: 6, paddingTop: 12, borderTop: RULE }} data-testid={`by-event-${ev.eventId}`}>
              <span className="flex flex-wrap items-center justify-between" style={{ gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{ev.title}</span>
                <span className="flex items-center" style={{ gap: 8 }}>
                  <span style={{ ...kicker(9), color: MUTED }}>{plural(ev.guestCount, ev.isTicketed ? 'TICKET HOLDER' : 'GOING')}</span>
                  <PillButton variant="tertiary" size={28} onClick={() => navigate(`/host/events/${ev.eventId}?tab=guests`)}>OPEN</PillButton>
                </span>
              </span>
              {ev.guests.length === 0 && <span style={{ fontSize: 11, color: SUBTLE }}>Nobody yet.</span>}
              <span className="flex flex-wrap" style={{ gap: 6 }}>{ev.guests.map((m) => <span key={m.userId} className="flex items-center" style={{ gap: 6, padding: '4px 10px 4px 4px', borderRadius: 999, background: INSET, fontSize: 11, fontWeight: 700 }}><Face src={m.avatarUrl} name={m.displayName} size={24} />{m.displayName}{m.quantity > 1 ? ` ×${m.quantity}` : ''}</span>)}</span>
            </div>
          ))}
        </div>
      )}
    </ClubPanel>
  );

  // ── APPLICATIONS ──
  const applications = (
    <ClubPanel phone={mobile} data-testid="applications-panel">
      <PanelHead title={g.mode === 'curated' ? 'ENTRY REQUESTS' : 'ENTRIES'} right={`${applicants.length} TOTAL · ${pending} WAITING`}>
        {open && <PillButton variant="tertiary" size={32} onClick={() => setCompOpen(true)} data-testid="comp-someone">COMP SOMEONE</PillButton>}
      </PanelHead>
      {applicants.length === 0 && <EmptyBlock title="NO ENTRIES YET." body={g.mode === 'curated' ? 'Requests land here for you to accept or decline.' : 'Entries land here as people get in.'} />}
      <div className="flex flex-col">
        {applicants.map((a) => {
          const [pl, pbg, pfg] = entryPill(a.status);
          const paid = a.principalCents > 0 && !a.refunded;
          const canReview = a.status === 'pending' || (['accepted', 'pending_payment'].includes(a.status) && !paid);
          const canRefund = native && paid && !a.isFrozen && ['paid', 'accepted', 'pending'].includes(a.status) && a.admissionId;
          return (
            <div key={a.admissionId || a.userId} className="flex flex-wrap items-center" style={{ gap: '6px 12px', padding: '10px 0', borderTop: RULE }} data-testid={`applicant-${a.userId}`}>
              <Face src={a.avatarUrl} name={a.displayName} size={36} />
              <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{a.displayName}</span>
                <span style={{ fontSize: 10, color: MUTED }}>{ago(a.appliedAt)}{a.quantity > 1 ? ` · ${a.quantity} SEATS` : ''}{a.source === 'comp' ? ' · COMPED' : paid ? ` · PAID ${money(a.principalCents)}` : ''}{a.isFrozen ? ' · FROZEN' : ''}</span>
              </span>
              <TagPill bg={pbg} fg={pfg} size={8} data-testid={`applicant-status-${a.userId}`}>{pl}</TagPill>
              <span className="flex" style={{ gap: 6 }}>
                {canReview && a.status === 'pending' && <PillButton size={32} disabled={!!busy} onClick={() => review(a.userId, 'accept')} data-testid={`accept-${a.userId}`}>ACCEPT</PillButton>}
                {canReview && <PillButton variant="tertiary" size={32} disabled={!!busy} onClick={() => review(a.userId, 'decline')} data-testid={`decline-${a.userId}`}>DECLINE</PillButton>}
                {canRefund && <PillButton variant={isArmed(`refund-${a.admissionId}`) ? 'armed' : 'danger'} size={32} disabled={busy === `refund:${a.admissionId}`} onClick={() => refund(a)} data-testid={`refund-${a.userId}`}>{isArmed(`refund-${a.admissionId}`) ? `REFUND ${money(a.principalCents)}?` : 'REFUND'}</PillButton>}
              </span>
            </div>
          );
        })}
      </div>
    </ClubPanel>
  );

  // ── DOOR ──
  const holders = admissions.filter((a) => a.status === 'paid');
  const doorMatches = holders.filter((a) => !q.trim() || a.displayName.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 12);
  const doorTab = native ? (
    <div className="flex flex-col" style={{ gap: 14 }} data-testid="door-tab">
      <DoorScannerCard scanner={scanner} size="tab" counter={`${roster?.seatsCheckedIn ?? 0} OF ${roster?.seatsTotal ?? 0} IN`} pad={mobile ? '18px' : '26px'}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#fff', opacity: 0.8, textTransform: 'uppercase' }}>GATHERING ENTRY ONLY · EVENT TICKETS SCAN AT THEIR OWN DOOR</span>
        <button type="button" className="door-btn-dark" style={{ ...btnDark, fontSize: 12, letterSpacing: '.16em' }} onClick={() => setAttendeeView((v) => (v === 'byname' ? 'person' : 'byname'))} aria-expanded={attendeeView === 'byname'} data-testid="door-by-name">CHECK IN BY NAME</button>
      </DoorScannerCard>
      {attendeeView === 'byname' && (
        <ClubPanel phone={mobile} data-testid="door-by-name-panel">
          <PanelHead title="CHECK IN BY NAME" right={`${holders.length} IN WITH SEATS`} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…" aria-label="Search entries" style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 999, background: INSET, fontFamily: 'inherit', fontSize: 13, outline: 'none', maxWidth: 360 }} />
          {holders.length === 0 ? <EmptyBlock title="NO SEATS YET." /> : doorMatches.length === 0 ? <EmptyBlock title="NO ONE MATCHES." /> : (
            <div className="flex flex-col">
              {doorMatches.map((a) => {
                const v = doorVerdicts[a.id];
                const allIn = a.ticketsCheckedIn >= a.ticketCount;
                return (
                  <div key={a.id} className="flex flex-wrap items-center" style={{ gap: '6px 12px', padding: '10px 0', borderTop: RULE }} data-testid={`door-row-${a.id}`}>
                    <Face src={a.avatarUrl} name={a.displayName} size={36} />
                    <span className="flex min-w-0 flex-col" style={{ flex: '1 1 160px', gap: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{a.displayName}</span>
                      <span style={{ fontSize: 10, color: MUTED }}>{plural(a.ticketCount, 'SEAT')} · {a.ticketsCheckedIn === 0 ? 'NOT HERE YET' : allIn ? 'IN' : `${a.ticketsCheckedIn} OF ${a.ticketCount} IN`}</span>
                    </span>
                    {v && <TagPill bg={v.result === 'ok' ? '#22C55E' : v.result === 'already' ? '#FACC15' : '#FEE2E2'} fg={v.result === 'ok' ? '#fff' : v.result === 'already' ? INK : RED} size={8}>{v.result === 'ok' ? 'VERIFIED' : v.result === 'already' ? 'ALREADY IN' : v.result === 'transferred' ? 'SEAT TRANSFERRED' : 'NO ENTRY'}</TagPill>}
                    {!allIn && <PillButton size={32} disabled={busy === `door:${a.id}`} onClick={() => doorCheckIn(a)} data-testid={`door-checkin-${a.id}`}>{busy === `door:${a.id}` ? 'CHECKING…' : 'CHECK IN'}</PillButton>}
                    {allIn && !v && <TagPill bg="#22C55E" fg="#fff" size={8}>IN</TagPill>}
                  </div>
                );
              })}
            </div>
          )}
        </ClubPanel>
      )}
    </div>
  ) : (
    <ClubPanel phone={mobile} data-testid="door-legacy">
      <PanelHead title="THE DOOR" />
      <span style={{ fontSize: 12, lineHeight: 1.6, color: BODY }}>This gathering's entry still runs on its pass event until the migration. Its scanner is the pass event's door; event tickets scan at each event's own door from THE AGENDA.</span>
      {g.passEventId && <PillButton size={40} onClick={() => navigate(`/host/events/${g.passEventId}/door`)} style={{ alignSelf: 'flex-start' }} data-testid="legacy-door">OPEN THE ENTRY DOOR</PillButton>}
    </ClubPanel>
  );

  // ── MONEY ──
  const m = moneyDto;
  const moneyTab = (
    <>
      <ClubPanel phone={mobile} data-testid="money-entry">
        <PanelHead title="GATHERING ENTRY" right={m ? (m.native ? `${lcLabel} · NATIVE` : 'VIA PASS EVENT') : null} />
        {!m && <span style={{ fontSize: 12, color: MUTED }}>Loading…</span>}
        {m && !hasEntry && <span style={{ fontSize: 12, color: MUTED }}>This gathering has no entry fee or approval — nothing is collected at the gathering level.</span>}
        {m && hasEntry && (
          <KVRows testId="money-rows" rows={[
            ['COLLECTED', money(m.collectedCents || 0)],
            ['REFUNDED', money(m.refundedCents || 0)],
            ...(m.refundPendingCents ? [['REFUNDING', money(m.refundPendingCents)]] : []),
            ...(m.frozenCents ? [['FROZEN · DISPUTED', money(m.frozenCents)]] : []),
            ...(m.feesCents ? [['PARTY STARTER FEE + GST', money(m.feesCents)]] : []),
            ['HELD IN ESCROW', money(m.heldCents || 0)],
            ['RELEASED TO YOU', money(m.releasedCents || 0)],
            ['RELEASE', m.escrowReleasedAt ? `RELEASED ${ago(m.escrowReleasedAt)}` : m.escrowReleaseAt ? `DUE ${whenLabel(m.escrowReleaseAt)}` : lifecycle === 'ended' ? 'SCHEDULED' : '24H AFTER IT ENDS'],
            ['ENTRIES', `${m.paidCount} IN · ${m.acceptedCount} ACCEPTED · ${m.pendingCount} WAITING · ${m.refundedCount} REFUNDED`],
          ]} />
        )}
        {m && hasEntry && !m.native && <span style={{ fontSize: 11, lineHeight: 1.6, color: MUTED }}>Settled figures come from the pass event's ledger until this gathering is migrated; open its MONEY tab for the detail.</span>}
      </ClubPanel>
      <ClubPanel phone={mobile} data-testid="money-children">
        <PanelHead title="EACH EVENT'S OWN MONEY" right="SEPARATE LEDGERS" />
        <span style={{ fontSize: 11, lineHeight: 1.6, color: BODY }}>Ticket money on the agenda belongs to each event. These are estimates from what was pledged; the settled figures are on each event's MONEY tab.</span>
        {(m?.childEvents || []).length === 0 && <span style={{ fontSize: 12, color: MUTED }}>No events on the agenda yet.</span>}
        <div className="flex flex-col">
          {(m?.childEvents || []).map((c) => (
            <div key={c.eventId} className="flex flex-wrap items-center" style={{ gap: '6px 12px', padding: '10px 0', borderTop: RULE }} data-testid={`child-money-${c.eventId}`}>
              <span className="flex min-w-0 flex-col" style={{ flex: '1 1 200px', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{c.title}</span>
                <span style={{ fontSize: 10, color: MUTED }}>{whenLabel(c.dateTime)} · {c.ticketPriceCents > 0 ? money(c.ticketPriceCents) : 'FREE'}</span>
              </span>
              <span style={{ fontSize: 12, fontWeight: 700 }}>{c.ticketsSold} SOLD · ≈ {money(c.estimatedRevenueCents)}</span>
              <PillButton variant="tertiary" size={28} onClick={() => navigate(`/host/events/${c.eventId}?tab=money`)}>ITS MONEY</PillButton>
            </div>
          ))}
        </div>
      </ClubPanel>
    </>
  );

  const body = tab === 'overview' ? overview : tab === 'attendees' ? attendees : tab === 'applications' ? applications : tab === 'door' ? doorTab : moneyTab;

  return frame(
    <>
      {header}
      {tabRow}
      <div className="flex min-w-0 flex-col" style={{ gap: mobile ? 10 : 16 }} data-testid="manager-body">{body}</div>
      <AddPeopleModal title="Comp free entry" ctaLabel="Comp" placeholder="Search people to comp…" isOpen={compOpen} onClose={() => setCompOpen(false)} onAdd={comp} excludeUserIds={[profile?.userId].filter(Boolean)} />
    </>,
  );
}
